/**
 * useLLMStream Hook
 *
 * Hook for streaming LLM chat completions with real-time token updates.
 */

import { useCallback, useRef } from 'react';
import { useAIGatewayStore } from '../stores/useAIGatewayStore';
import { aiGatewayService } from '../services/aiGateway';
import type { LLMMessage, GenerationParams, AIGatewayError } from '../types/aiGateway';

export interface UseLLMStreamOptions {
  onToken?: (token: string) => void;
  onComplete?: (fullText: string, tokenCount: number) => void;
  onError?: (error: AIGatewayError) => void;
}

export function useLLMStream() {
  const {
    isStreaming,
    streamedText,
    currentStreamId,
    setIsStreaming,
    setCurrentStreamId,
    setStreamedText,
    appendStreamedText,
    setError,
  } = useAIGatewayStore();

  const abortRef = useRef(false);

  /**
   * Start a streaming chat completion
   */
  const streamChat = useCallback(
    async (
      messages: LLMMessage[],
      options?: UseLLMStreamOptions & { generationParams?: GenerationParams }
    ): Promise<string> => {
      // Ensure connected
      if (!aiGatewayService.isConnected()) {
        try {
          await aiGatewayService.connect();
        } catch (error) {
          const gatewayError = error as AIGatewayError;
          options?.onError?.(gatewayError);
          throw error;
        }
      }

      abortRef.current = false;
      setIsStreaming(true);
      setStreamedText('');

      try {
        const result = await aiGatewayService.streamChat(
          messages,
          {
            onStart: () => {
              setCurrentStreamId(Date.now().toString());
            },
            onDelta: (content) => {
              if (!abortRef.current) {
                appendStreamedText(content);
                options?.onToken?.(content);
              }
            },
            onComplete: (fullResponse, tokenCount) => {
              setIsStreaming(false);
              setCurrentStreamId(null);
              options?.onComplete?.(fullResponse, tokenCount);
            },
            onError: (error) => {
              setIsStreaming(false);
              setCurrentStreamId(null);
              setError(error);
              options?.onError?.(error);
            },
          },
          options?.generationParams
        );

        return result;
      } catch (error) {
        setIsStreaming(false);
        setCurrentStreamId(null);
        throw error;
      }
    },
    [
      setIsStreaming,
      setStreamedText,
      setCurrentStreamId,
      appendStreamedText,
      setError,
    ]
  );

  /**
   * Cancel the current stream
   */
  const cancelStream = useCallback(() => {
    abortRef.current = true;
    setIsStreaming(false);
    setCurrentStreamId(null);
    aiGatewayService.interrupt();
  }, [setIsStreaming, setCurrentStreamId]);

  /**
   * Clear the streamed text
   */
  const clearStreamedText = useCallback(() => {
    setStreamedText('');
  }, [setStreamedText]);

  return {
    // State
    isStreaming,
    streamedText,
    currentStreamId,

    // Actions
    streamChat,
    cancelStream,
    clearStreamedText,
  };
}
